import React from 'react';

interface StatuteNodeProps {
  data: any;
}

/** Strip common PDF extraction noise; does not restructure poorly chunked text. */
function sanitizePlainStatuteText(raw: string): string {
  return raw
    .replace(/\bPage\s+\d+\s+of\s+\d+\b/gi, '\n')
    .replace(/\f/g, '\n')
    .replace(/\n[ \t]+\n/g, '\n\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export function StatuteRenderer({ data }: StatuteNodeProps) {
  if (!data) return null;

  // If the data itself is a string, just render it.
  if (typeof data === 'string') {
    return (
      <div className="whitespace-pre-wrap mb-4">{sanitizePlainStatuteText(data)}</div>
    );
  }

  // If it's an array, render each item
  if (Array.isArray(data)) {
    return (
      <div className="space-y-4">
        {data.map((item, i) => (
          <StatuteRenderer key={i} data={item} />
        ))}
      </div>
    );
  }

  // Expect object
  const { content, subsections, clauses, provisos, explanations, sub_sections, subSections } = data;

  const actualSubsections = subsections || sub_sections || subSections;

  return (
    <div className="space-y-3">
      {content && (
        <div className="whitespace-pre-wrap text-[#334155] leading-relaxed text-justify hyphens-auto">
          {typeof content === 'string' ? sanitizePlainStatuteText(content) : content}
        </div>
      )}

      {/* Subsections */}
      {actualSubsections && Array.isArray(actualSubsections) && (
        <div className="pl-6 space-y-3">
          {actualSubsections.map((sub: any, i: number) => (
            <div key={i} className="flex gap-3">
              {(sub.number || sub.id) && (
                <span className="font-semibold text-slate-700 shrink-0 select-none">
                  ({sub.number || sub.id})
                </span>
              )}
              <div className="flex-1 text-justify hyphens-auto">
                <StatuteRenderer data={sub} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Clauses */}
      {clauses && Array.isArray(clauses) && (
        <div className="pl-6 space-y-2 mt-2">
          {clauses.map((clause: any, i: number) => (
            <div key={i} className="flex gap-3">
              {(clause.number || clause.letter || clause.id) && (
                <span className="font-medium text-slate-600 shrink-0 select-none">
                  ({clause.number || clause.letter || clause.id})
                </span>
              )}
              <div className="flex-1 text-justify hyphens-auto">
                <StatuteRenderer data={clause} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Provisos */}
      {provisos && Array.isArray(provisos) && (
        <div className="mt-4 space-y-2">
          {provisos.map((proviso: any, i: number) => (
            <div
              key={i}
              className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg text-amber-900 text-sm leading-relaxed text-justify hyphens-auto"
            >
              <span className="font-bold mr-2 uppercase tracking-wide text-amber-700 text-xs">Proviso</span>
              {typeof proviso === 'string' ? proviso : <StatuteRenderer data={proviso} />}
            </div>
          ))}
        </div>
      )}

      {/* Explanations */}
      {explanations && Array.isArray(explanations) && (
        <div className="mt-4 space-y-2">
          {explanations.map((exp: any, i: number) => (
            <div
              key={i}
              className="bg-slate-100 border-l-4 border-slate-400 p-4 rounded-r-lg text-slate-800 text-sm leading-relaxed text-justify hyphens-auto"
            >
              <span className="font-bold mr-2 uppercase tracking-wide text-slate-500 text-xs">Explanation</span>
              {typeof exp === 'string' ? exp : <StatuteRenderer data={exp} />}
            </div>
          ))}
        </div>
      )}

      {/* Fallback for other keys if needed, but usually we just want to render known structure */}
    </div>
  );
}
